import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import { apiClient } from '../../services/apiClient';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.post('/api/feedback/', {
        feedback_text: feedbackText.trim(),
        phone_number: phoneNumber.trim() || null
      });

      setSuccessMessage('Thank you for your feedback! We appreciate your input and will respond as quickly as possible.');
      setTimeout(() => {
        handleClose();
        setSuccessMessage(null);
      }, 2000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFeedbackText('');
      setPhoneNumber('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          // Mobile adjustments
          '@media (max-width: 600px)': {
            margin: '16px',
            maxHeight: 'calc(100vh - 32px)',
            height: 'auto'
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        Feedback / Let us know if something doesn't look correct
      </DialogTitle>
      
      <DialogContent sx={{ 
        pt: 5, 
        pb: 2,
        // Mobile adjustments for when keyboard is open
        '@media (max-width: 600px)': {
          pt: 3,
          pb: 1,
          px: 2
        }
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Your Feedback"
          multiline
          rows={5}
          fullWidth
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          placeholder="Please share your thoughts, report issues, or suggest improvements..."
          required
          disabled={isSubmitting}
          InputLabelProps={{
            shrink: true
          }}
          sx={{ 
            mb: 1, 
            mt: error ? 0 : 2,
            // Reduce rows on mobile when keyboard is likely to be open
            '& .MuiInputBase-inputMultiline': {
              '@media (max-width: 600px) and (max-height: 700px)': {
                minHeight: '80px !important',
                maxHeight: '120px'
              }
            }
          }}
        />
        
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            color: 'text.secondary',
            mb: 2,
            fontStyle: 'italic'
          }}
        >
          We will also submit a snapshot of the forecast at the time you submit this feedback
        </Typography>

        <TextField
          label="Contact Phone Number (Optional)"
          fullWidth
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="If you'd like us to call you back"
          disabled={isSubmitting}
          InputLabelProps={{
            shrink: true
          }}
          sx={{ mb: 2 }}
          helperText="Optional - if you'd prefer us to call you back. Otherwise we will respond to your email"
        />

        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'primary.light',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <PhoneIcon sx={{ color: 'primary.contrastText' }} />
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'primary.contrastText',
                fontWeight: 500 
              }}
            >
              Need immediate assistance?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'primary.contrastText',
                fontWeight: 700
              }}
            >
              Call us 24/7: +64 9 888 0558
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !feedbackText.trim()}
          startIcon={isSubmitting && <CircularProgress size={20} />}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={2000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};